import { FC, useEffect, useMemo, useState } from 'react';
import { Content as Wrapper } from '../../../../../../../../../shared/components/content/content.component.tsx';
import { FormProvider, useForm } from 'react-hook-form';
import {
  FormTextFieldWithButton,
  FormTextEditorWithButton,
} from '../../../../../../../../../shared/components/form';
import { Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fontHelper } from '../../../../../../../../../shared/helpers/font/font.helper.ts';
import { IPopupWrapper } from '../../../../../../../../../shared/components/popup/components/popup.component.tsx';
import {
  dictionaryAdditionalService,
  GetDictDebtReminderRes,
} from '../../../../../../../../../shared/components/dictionary/services/dictionaryAdditional.service.ts';
import { responseHandler } from '../../../../../../../../../shared/responseHandler/responseHandler.ts';
import { take } from 'rxjs';
import { dictionaryState } from '../../../../../../../../../shared/components/dictionary/states/dictionary.state.ts';
import { useBeforeClosePopup } from '../../../../../../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';
import TranslateIcon from '@mui/icons-material/Translate';
import { TranslationPopup } from '../../../../../../../../../shared/components/popup/components/translation/translation.popup.tsx';
import { C_Content_Translation_Type } from '../../../../../../../../../graphql/generatedModel.ts';
import {
  configsData,
  DictTranslationLanguagesQueryRes,
} from '../../../../../../../../../shared/services/configsData/configsData.service.ts';

export const Content: FC<IContent> = ({ stream }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [id, setId] = useState<string>('');
  const [translationLanguages, setTranslationLanguages] =
    useState<DictTranslationLanguagesQueryRes>([]);
  const defaultFontSize = useMemo(() => fontHelper.getDefaultFont(['fontSize']), []);
  const descriptionTranslationPopup = useMemo(() => new TranslationPopup(), []);
  const reminderTextTranslationPopup = useMemo(() => new TranslationPopup(), []);

  const formMethods = useForm<GetDictDebtReminderRes>({
    mode: 'onSubmit',
    defaultValues: {
      reminderText: '',
      description: '',
      reminderTextTranslations: [],
      descriptionTranslations: [],
    },
  });

  const {
    formState: { isDirty },
    setValue,
    getValues,
  } = formMethods;

  useEffect(() => {
    const langSub = configsData.sub
      .dictTranslationLanguages()
      .pipe(take(1))
      .subscribe((langs) => {
        setTranslationLanguages(langs || []);
      });
    configsData.pub.common(['dictTranslationLanguages']);

    const dictionaryAdditionalSub = dictionaryAdditionalService.sub
      .getDictDebtReminder()
      .pipe(
        responseHandler<GetDictDebtReminderRes>({
          errorReturnType: {
            reminderText: '',
            description: '',
            reminderTextTranslations: [],
            descriptionTranslations: [],
          } as GetDictDebtReminderRes,
        }),
      )
      .subscribe((formData) => {
        formMethods.reset({
          ...formData,
          reminderTextTranslations: formData?.reminderTextTranslations || [],
          descriptionTranslations: formData?.descriptionTranslations || [],
        });
        setLoading(false);
      });

    stream
      .actionListener('open')
      .pipe(take(1))
      .subscribe(({ id }) => {
        dictionaryAdditionalService.pub.getDictDebtReminder({ id });
        setId(id);
      });

    const descriptionTranslationSub = descriptionTranslationPopup.stream.state().subscribe((v) => {
      if (v?.action === 'reset') {
        setValue('descriptionTranslations', v?.values?.translations || []);
      }
      if (v?.action === 'recordData') {
        setValue('descriptionTranslations', v?.values?.translations || [], { shouldDirty: true });
      }
    });

    const reminderTextTranslationSub = reminderTextTranslationPopup.stream
      .state()
      .subscribe((v) => {
        if (v?.action === 'reset') {
          setValue('reminderTextTranslations', v?.values?.translations || []);
        }
        if (v?.action === 'recordData') {
          setValue('reminderTextTranslations', v?.values?.translations || [], {
            shouldDirty: true,
          });
        }
      });

    return () => {
      dictionaryAdditionalSub.unsubscribe();
      descriptionTranslationSub.unsubscribe();
      reminderTextTranslationSub.unsubscribe();
      descriptionTranslationPopup.stream.unsubscribe();
      reminderTextTranslationPopup.stream.unsubscribe();
      langSub.unsubscribe();
    };
  }, []);

  useBeforeClosePopup({
    stream,
    isDirty,
  });

  const onSubmit = (formData: GetDictDebtReminderRes) => {
    const defaultValues = formMethods.formState.defaultValues;
    const changed =
      defaultValues?.reminderText !== formData.reminderText ||
      defaultValues?.description !== formData.description ||
      JSON.stringify(defaultValues?.reminderTextTranslations) !==
        JSON.stringify(formData.reminderTextTranslations) ||
      JSON.stringify(defaultValues?.descriptionTranslations) !==
        JSON.stringify(formData.descriptionTranslations);

    if (changed) {
      dictionaryState.pub.updateCell(
        {
          id,
          field: 'virtualField',
          value: formData,
        },
        true,
      );
    }
    stream.emit('close');
  };

  return (
    <Wrapper type='box' {...{ loading }} boxProps={{ sx: { minHeight: '298px' } }}>
      <FormProvider {...formMethods}>
        <form
          style={{ opacity: loading ? '0' : '1', transition: '0.3s' }}
          onSubmit={formMethods.handleSubmit(onSubmit)}
          noValidate
        >
          {!loading && (
            <>
              <FormTextFieldWithButton
                slotProps={{ htmlInput: { maxLength: 40 } }}
                name='description'
                label={t('settings.report_header')}
                sx={{ mb: 3 }}
                width='100%'
                buttonVisibility='hoverOrFocus'
                buttonIcon={<TranslateIcon />}
                buttonTooltip={t('common.translations')}
                onButtonClick={() =>
                  descriptionTranslationPopup.stream.emit('open', {
                    values: { translations: getValues().descriptionTranslations },
                    languages: translationLanguages,
                    contentType: C_Content_Translation_Type.DEBT_REMINDER_DESCRIPTION,
                    originalContent: getValues().description ?? '',
                    isRichText: false,
                    maxLength: 40,
                  })
                }
              />
              <FormTextEditorWithButton
                name='reminderText'
                defaultFormat={defaultFontSize}
                buttonVisibility='hoverOrFocus'
                buttonIcon={<TranslateIcon />}
                buttonTooltip={t('common.translations')}
                onButtonClick={() =>
                  reminderTextTranslationPopup.stream.emit('open', {
                    values: { translations: getValues().reminderTextTranslations },
                    languages: translationLanguages,
                    contentType: C_Content_Translation_Type.DEBT_REMINDER_TEXT,
                    originalContent: getValues().reminderText ?? '',
                    isRichText: true,
                  })
                }
              />
              <Stack
                direction='row'
                spacing={2}
                sx={{
                  mt: 3,
                  justifyContent: 'flex-end',
                }}
              >
                <Button type='submit' variant='contained'>
                  {t('common.save')}
                </Button>
              </Stack>
            </>
          )}
        </form>
      </FormProvider>
      <descriptionTranslationPopup.Component />
      <reminderTextTranslationPopup.Component />
    </Wrapper>
  );
};

interface IContent {
  stream: IPopupWrapper['stream'];
}
